// filepath: apps/web/src/admin/people/PersonForm.tsx
//
// ISS-518 — shared person create/edit form component.
//
// BA spec (note 18233):
//   - Slice 1 form's source of truth = `display_name` (required
//     server-side), `email`, `phone`, `status` (where server
//     allows it).
//   - `display_name` MUST be non-empty server-side; the form
//     layer enforces.
//   - `status` "where server allows" — for create the server
//     defaults to "active" (per Go's typed-column default); for
//     edit the form passes the current value through.
//
// INV-9: no dangerouslySetInnerHTML.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Card } from "@/design-system/Card";
import { Button } from "@/design-system/Button";
import { Banner } from "@/design-system/Banner";
import { useDirtyTrackedFields } from "@/lib/useDirtyTrackedFields";

export interface PersonFormValues {
  display_name: string;
  email: string;
  phone: string;
  status: "active" | "inactive" | "archived";
}

/**
 * The shape handed to `useDirtyTrackedFields`. A `type` alias, not the
 * exported `interface` above: only the former gets an implicit index
 * signature, which the hook's `T extends Record<string, unknown>`
 * bound needs.
 */
type PersonFormFields = {
  display_name: string;
  email: string;
  phone: string;
  status: PersonFormValues["status"];
};

export interface PersonFormProps {
  initial: PersonFormValues;
  /** Submit handler. The form passes the trimmed values; the
   * page wires this to createPerson / updatePerson. */
  onSubmit: (values: PersonFormValues) => Promise<void>;
  onCancel?: () => void;
  submitting?: boolean;
  error?: string | null;
  submitLabel: string;
  loadingLabel: string;
  heading: string;
}

export function PersonForm({
  initial,
  onSubmit,
  onCancel,
  submitting,
  error,
  submitLabel,
  loadingLabel,
  heading,
}: PersonFormProps): React.ReactElement {
  const { t } = useTranslation();

  // ISS-805 — per-field dirty tracking replaces the whole-state re-seed effect
  // that used to sit here on [initial.display_name, initial.email,
  // initial.phone, initial.status]. That effect reset ALL FOUR fields when
  // ANY ONE dep moved, so an email address changing in another session wiped
  // the display name and phone the operator was typing. Each field now
  // re-seeds only on its OWN server value, and never once it has been edited
  // (see lib/useDirtyTrackedFields for the BA ruling).
  //
  // `recordKey` is a primitive, never the `initial` object (a new identity
  // every render would re-seed on every render). This form carries no record
  // id, so its identity IS its mount: the edit screen keys it by row id, and
  // pointing the route at a different person remounts the form, which is
  // rule 1's "different record" path.
  const fields = useDirtyTrackedFields<PersonFormFields>(initial, "person");

  // BA rule 4 — a non-blocking notice, never a dialog: when the server value
  // moved underneath a field the operator HAS edited, their text is kept and
  // the server's value is shown beside it. Dismiss clears it per field.
  const conflictFor = (
    id: string,
    label: string,
    field: {
      serverConflictValue: string | undefined;
      acknowledgeServerConflict: () => void;
    },
  ): {
    id: string;
    label: string;
    value: string;
    dismiss: () => void;
  } | null =>
    field.serverConflictValue === undefined
      ? null
      : {
          id,
          label,
          value: field.serverConflictValue,
          dismiss: field.acknowledgeServerConflict,
        };

  const serverConflicts = [
    conflictFor(
      "display_name",
      t("directory.people.form.display_name", "Display name"),
      fields.display_name,
    ),
    conflictFor(
      "email",
      t("directory.people.form.email", "Email"),
      fields.email,
    ),
    conflictFor(
      "phone",
      t("directory.people.form.phone", "Phone"),
      fields.phone,
    ),
    conflictFor(
      "status",
      t("directory.people.form.status", "Status"),
      fields.status,
    ),
  ].filter((c): c is NonNullable<typeof c> => c !== null);

  return (
    <section data-testid="person-form-screen">
      <header>
        <h1>{heading}</h1>
      </header>
      {error ? (
        // design-adherence-ignore: <Banner> doesn't accept data-testid;
        // hook the test id on a wrapping div instead.
        <div data-testid="person-form-error">
          <Banner tone="danger">
            {t("directory.people.form.error", "Error: {{message}}", {
              message: error,
            })}
          </Banner>
        </div>
      ) : null}
      {serverConflicts.length > 0 ? (
        <div data-testid="person-form-server-conflicts">
          {serverConflicts.map((c) => (
            <Banner key={c.id} tone="warning" onDismiss={c.dismiss}>
              {t(
                "directory.people.form.server_conflict",
                "{{field}} changed on the server while you were editing. Your text was kept — the server now holds: {{value}}",
                { field: c.label, value: c.value },
              )}
            </Banner>
          ))}
        </div>
      ) : null}
      <Card>
        <form
          aria-label={heading}
          onSubmit={async (e) => {
            e.preventDefault();
            await onSubmit({
              display_name: fields.display_name.value.trim(),
              email: fields.email.value.trim(),
              phone: fields.phone.value.trim(),
              status: fields.status.value,
            });
          }}
        >
          <div>
            <label htmlFor="person-form-display-name">
              {t("directory.people.form.display_name", "Display name")}
            </label>
            <input
              id="person-form-display-name"
              required
              minLength={1}
              maxLength={255}
              value={fields.display_name.value}
              onChange={(e) => fields.display_name.set(e.target.value)}
              data-testid="person-form-display-name"
            />
          </div>
          <div>
            <label htmlFor="person-form-email">
              {t("directory.people.form.email", "Email")}
            </label>
            <input
              id="person-form-email"
              type="email"
              maxLength={320}
              value={fields.email.value}
              onChange={(e) => fields.email.set(e.target.value)}
              data-testid="person-form-email"
            />
          </div>
          <div>
            <label htmlFor="person-form-phone">
              {t("directory.people.form.phone", "Phone")}
            </label>
            <input
              id="person-form-phone"
              type="tel"
              maxLength={64}
              value={fields.phone.value}
              onChange={(e) => fields.phone.set(e.target.value)}
              data-testid="person-form-phone"
            />
          </div>
          <div>
            <label htmlFor="person-form-status">
              {t("directory.people.form.status", "Status")}
            </label>
            <select
              id="person-form-status"
              value={fields.status.value}
              onChange={(e) =>
                fields.status.set(e.target.value as PersonFormValues["status"])
              }
              data-testid="person-form-status"
            >
              <option value="active">
                {t("directory.people.status.active", "Active")}
              </option>
              <option value="inactive">
                {t("directory.people.status.inactive", "Inactive")}
              </option>
              <option value="archived">
                {t("directory.people.status.archived", "Archived")}
              </option>
            </select>
          </div>
          <div className="person-form-actions">
            {/* design-adherence-ignore: <Button> doesn't accept data-testid; wrap. */}
            <span data-testid="person-form-submit">
              <Button
                type="submit"
                variant="primary"
                disabled={
                  submitting || fields.display_name.value.trim().length === 0
                }
              >
                {submitting ? loadingLabel : submitLabel}
              </Button>
            </span>
            {onCancel ? (
              <span data-testid="person-form-cancel">
                <Button type="button" variant="secondary" onClick={onCancel}>
                  {t("directory.people.form.cancel", "Cancel")}
                </Button>
              </span>
            ) : null}
          </div>
        </form>
      </Card>
    </section>
  );
}
